<template>
  <div class="cases-category">
    <div v-if="title" class="category-title">
      <span>{{ title }}</span>
    </div>
    <div class="content">
      <template v-for="caseItem of cases" :key="caseItem.id">
        <slot :case="caseItem">
          <router-link :to="{ name: 'case-detail', params: { caseName: encodeCaseName(caseItem.name) } }">
            <SingleCase :caseData="caseItem" :showWpIconOnMainPage="caseItem.isEventFeatured"
              :wpEarnings="calculateWp(caseItem)" />
          </router-link>
        </slot>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { usePromotionalEventsStore } from '@/store/promotionalEvents'
import type { Case } from '@/services/models/models'
import SingleCase from '@/views/Cases/components/SingleCase.vue'
import { encodeCaseName } from '@/utils/caseNameUrl'

interface Props {
  cases: any[]
  title?: string
}

defineProps<Props>()

const DEFAULT_CASE_OPEN_BASIS_POINTS = 200

const promotionalEventsStore = usePromotionalEventsStore()
const { currentEvent, hasActiveEvent } = storeToRefs(promotionalEventsStore)

const caseOpenBasisPoints = computed(
  () => currentEvent.value?.caseOpenWp ?? DEFAULT_CASE_OPEN_BASIS_POINTS,
)
const featuredMultiplier = computed(() => currentEvent.value?.featuredMultiplier ?? 1)

function calculateWp(caseItem: Case): number {
  if (!hasActiveEvent.value || !caseItem?.isEventFeatured) return 0
  const price = Number(caseItem?.price) || 0
  if (price <= 0) return 0

  const baseWp = (price * caseOpenBasisPoints.value) / 10000
  return Math.round(baseWp * featuredMultiplier.value)
}
</script>

<style scoped lang="scss">
.cases-category {
  .category-title {
    color: #ffffff;
    font-weight: bold;
    text-transform: uppercase;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    background-color: $surface;
    font-size: 1.5em;
    letter-spacing: 1px;
    margin-bottom: 1em;
    width: 100%;
    gap: 1em;

    &::after {
      content: '';
      flex: 1;
      height: 4px;
      background: linear-gradient(to right, rgba(255, 255, 255, 0.2), transparent);
    }

    @include tablet {
      justify-content: center;
      text-align: center;

      &::after {
        display: none;
      }
    }
  }

  .content {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(214px, 1fr));
    gap: 1.1em;
    position: relative;
    width: 100%;
    margin: 0 auto;
    margin-bottom: 2em;

    a {
      display: block;
      min-width: 0;
      text-decoration: none;
    }

    @media (max-width: 1536px) {
      gap: 1em;
    }

    :deep(.singleCase-wrapper--image-container) {
      width: 100%;
      height: auto;
      aspect-ratio: 214 / 297;
    }

    @include mobile {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0.85em;
    }
  }
}
</style>
